import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  Layout,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import * as api from "../api";
import type { ReplayLogItem } from "../api";
import { LAYOUT } from "../constants/layout";
import { handleCopyText } from "../utils/textUtils";

const decodeBase64 = (value?: string): string => {
  if (!value) return "";
  try {
    const binary = window.atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch (error) {
    return value;
  }
};

const textBlockStyle: React.CSSProperties = {
  margin: 0,
  maxHeight: 420,
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: "#111827",
  border: "1px solid #30363d",
  borderRadius: 6,
  padding: 12,
  color: "#d1d5db",
};

const diffValueStyle = (different: boolean): React.CSSProperties => ({
  ...textBlockStyle,
  maxHeight: 220,
  minWidth: 0,
  borderColor: different ? "#ff4d4f" : "#30363d",
  background: different ? "rgba(255, 77, 79, 0.12)" : "#111827",
  color: different ? "#ffccc7" : "#d1d5db",
});

const tryParseJson = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const stringifyValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value === undefined) return "<缺失>";
  return JSON.stringify(value, null, 2);
};

const flattenJson = (
  value: unknown,
  prefix = "",
  result: Record<string, unknown> = {}
): Record<string, unknown> => {
  if (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      flattenJson(child, prefix ? `${prefix}.${key}` : key, result);
    });
    return result;
  }

  if (Array.isArray(value)) {
    value.forEach((child, index) => {
      flattenJson(child, `${prefix}[${index}]`, result);
    });
    if (value.length === 0) {
      result[prefix || "$"] = [];
    }
    return result;
  }

  result[prefix || "$"] = value;
  return result;
};

const buildJsonDiffRows = (sampleText: string, targetText: string) => {
  const sampleJson = tryParseJson(sampleText);
  const targetJson = tryParseJson(targetText);
  if (sampleJson === null || targetJson === null) return null;

  const sampleFlat = flattenJson(sampleJson);
  const targetFlat = flattenJson(targetJson);
  const paths = Array.from(
    new Set([...Object.keys(sampleFlat), ...Object.keys(targetFlat)])
  ).sort();

  return paths.map((path) => {
    const sampleValue = sampleFlat[path];
    const targetValue = targetFlat[path];
    return {
      path,
      sample: stringifyValue(sampleValue),
      target: stringifyValue(targetValue),
      different: stringifyValue(sampleValue) !== stringifyValue(targetValue),
    };
  });
};

const ReplayResults: React.FC = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialTaskId = query.get("task_id") || query.get("taskId") || "";

  const [taskId, setTaskId] = useState(initialTaskId);
  const [requestId, setRequestId] = useState("");
  const [recordRequestId, setRecordRequestId] = useState("");
  const [data, setData] = useState<ReplayLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [detail, setDetail] = useState<ReplayLogItem | null>(null);
  const [diffVisible, setDiffVisible] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffData, setDiffData] =
    useState<api.DiffReplayBodyResponse["data"] | null>(null);

  const fetchReplayLogs = useCallback(
    async (
      pageNum = pagination.current || 1,
      pageSize = pagination.pageSize || 10
    ) => {
      setLoading(true);
      try {
        const res = await api.listReplayLog({
          page_num: pageNum,
          page_size: pageSize,
          task_id: taskId || undefined,
          request_id: requestId || undefined,
          record_request_id: recordRequestId || undefined,
        });
        setData(res.data.items || []);
        setPagination({
          current: res.data.page_num,
          pageSize: res.data.page_size,
          total: res.data.total_count,
        });
      } catch (error) {
        message.error("获取回放结果失败");
        setData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination.current, pagination.pageSize, recordRequestId, requestId, taskId]
  );

  useEffect(() => {
    fetchReplayLogs(1, pagination.pageSize || 10);
  }, []);

  const openDiff = async (record: ReplayLogItem) => {
    setDiffLoading(true);
    setDiffVisible(true);
    setDiffData(null);
    try {
      const res = await api.diffReplayBody({
        request_id: record.request_id,
        record_request_id: record.record_request_id,
      });
      setDiffData(res.data);
    } catch (error) {
      message.error("获取 Diff 数据失败");
    } finally {
      setDiffLoading(false);
    }
  };

  const columns: ColumnsType<ReplayLogItem> = [
    {
      title: "回放任务",
      dataIndex: "task_id",
      width: 220,
      render: (value: string) => value ? <Tag color="blue">{value}</Tag> : "-",
    },
    {
      title: "回放请求 ID",
      dataIndex: "request_id",
      width: 260,
      ellipsis: true,
    },
    {
      title: "原始请求 ID",
      dataIndex: "record_request_id",
      width: 260,
      ellipsis: true,
    },
    {
      title: "请求",
      dataIndex: "request",
      render: (value: string) => {
        const text = decodeBase64(value);
        return (
          <Typography.Text ellipsis style={{ maxWidth: 520 }}>
            {text.split("\n")[0] || "-"}
          </Typography.Text>
        );
      },
    },
    {
      title: "操作",
      key: "action",
      width: 220,
      fixed: "right",
      render: (_: unknown, record) => (
        <Space>
          <Button size="small" onClick={() => setDetail(record)}>
            详情
          </Button>
          <Button size="small" onClick={() => openDiff(record)}>
            Diff
          </Button>
          <Button
            size="small"
            onClick={() => handleCopyText(record.record_request_id, "原始请求 ID")}
          >
            复制
          </Button>
        </Space>
      ),
    },
  ];

  const renderDiffSection = (
    title: string,
    sampleValue: string,
    targetValue: string
  ) => {
    const sampleText = decodeBase64(sampleValue);
    const targetText = decodeBase64(targetValue);
    const rows = buildJsonDiffRows(sampleText, targetText);

    if (!rows) {
      const different = sampleText !== targetText;
      return (
        <Space direction="vertical" style={{ width: "100%" }} size="small">
          <Typography.Title level={5}>{title}</Typography.Title>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <pre style={diffValueStyle(different)}>{sampleText}</pre>
            <pre style={diffValueStyle(different)}>{targetText}</pre>
          </div>
        </Space>
      );
    }

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="small">
        <Typography.Title level={5}>{title}</Typography.Title>
        <Table
          rowKey="path"
          size="small"
          pagination={false}
          tableLayout="fixed"
          dataSource={rows}
          columns={[
            {
              title: "字段",
              dataIndex: "path",
              width: 180,
              fixed: "left",
            },
            {
              title: "原始值",
              dataIndex: "sample",
              width: 420,
              render: (value: string, row) => (
                <pre style={diffValueStyle(row.different)}>{value}</pre>
              ),
            },
            {
              title: "回放值",
              dataIndex: "target",
              width: 420,
              render: (value: string, row) => (
                <pre style={diffValueStyle(row.different)}>{value}</pre>
              ),
            },
          ]}
          scroll={{ x: 1020, y: 360 }}
        />
      </Space>
    );
  };

  return (
    <Layout.Content style={{ padding: LAYOUT.CONTENT.PADDING }}>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Space wrap>
          <Input
            placeholder="回放任务 ID"
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Input
            placeholder="回放请求 ID"
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Input
            placeholder="原始请求 ID"
            value={recordRequestId}
            onChange={(e) => setRecordRequestId(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
          <Button type="primary" onClick={() => fetchReplayLogs(1, pagination.pageSize || 10)}>
            搜索
          </Button>
          <Button onClick={() => window.location.assign(window.location.pathname)}>
            返回录制列表
          </Button>
        </Space>

        <Table<ReplayLogItem>
          rowKey={(record) => record.request_id}
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={(next) => fetchReplayLogs(next.current, next.pageSize)}
          scroll={{ x: "max-content" }}
          bordered
        />
      </Space>

      <Drawer
        open={!!detail}
        title="回放详情"
        width="70vw"
        onClose={() => setDetail(null)}
      >
        {detail && (
          <Tabs
            items={[
              {
                key: "request",
                label: "Request",
                children: <pre style={textBlockStyle}>{decodeBase64(detail.request)}</pre>,
              },
              {
                key: "response",
                label: "Response",
                children: <pre style={textBlockStyle}>{decodeBase64(detail.response)}</pre>,
              },
              {
                key: "meta",
                label: "Meta",
                children: <pre style={textBlockStyle}>{decodeBase64(detail.meta)}</pre>,
              },
            ]}
          />
        )}
      </Drawer>

      <Modal
        open={diffVisible}
        title="回放 Diff"
        width="90vw"
        footer={null}
        onCancel={() => setDiffVisible(false)}
        loading={diffLoading}
      >
        {diffData && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {renderDiffSection(
              "Request URL / Body",
              diffData.sample.request,
              diffData.target.request
            )}
            {renderDiffSection(
              "Response Body",
              diffData.sample.response,
              diffData.target.response
            )}
          </Space>
        )}
      </Modal>
    </Layout.Content>
  );
};

export default ReplayResults;
